import { EventTypes } from '@candulabs/craft';
import {
  AlertDangerIcon,
  AlertWarningIcon,
  Button,
  EllipsisText,
  Feedback,
  TargetIcon,
} from 'canoe';
import { useFormikContext } from 'formik';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ValueBox } from '@candu-ai/craft/ui';
import { ContentPlacement, TooltipPlacement } from '@candu-ai/placement/types';
import { useActiveTooltip } from 'craft/components/Tooltip';
import {
  CompletedStepSettings,
  EditLinkButton,
  InlineMessage,
  RetargetButton,
  SelectorInputWrapper,
  SelectorValueInput,
} from 'craft/containers/Toolbox/tabs/Placements/components/ui';
import { FormState } from 'craft/containers/Toolbox/tabs/Placements/constants';
import { checkInvalidSelector } from 'craft/containers/Toolbox/tabs/Placements/utils';
import { useEditor, useMutations, useSelection, useTooltipPlacement } from 'craft/hooks';
import {
  setForcePlacementsTab,
  setPreviewSelector,
  setToolboxTab,
  useToolboxTab,
} from 'craft/state';
import { ToolboxTabs } from 'craft/types';

interface Props {
  hotspotId: string;
  contentPlacement: ContentPlacement;
  stepOneSelector?: string;
  handleRetargetClick: () => void;
  onSave: () => void;
}

export const TooltipHTMLSelector = ({
  hotspotId,
  contentPlacement,
  handleRetargetClick,
  stepOneSelector,
  onSave,
}: Props) => {
  const dispatch = useDispatch();
  const { deleteNode } = useMutations();
  const { forcePlacementsTab } = useToolboxTab();
  const { getSerializedTree } = useSelection();
  const { placement, setPlacement, addPlacement } = useTooltipPlacement(hotspotId);
  const { resetForm } = useFormikContext<ContentPlacement>();
  const hotspotSelectorRef = useRef<HTMLInputElement>(null);
  const { actions } = useEditor();

  const activeHotspot = useActiveTooltip();
  const tree = getSerializedTree(activeHotspot?.data.parent || hotspotId);
  const hotspotNodeIds = tree.nodes[tree.rootNodeId].nodes;
  const currentHotspotIndex = hotspotNodeIds.indexOf(hotspotId);
  const isLastHotspotsIdsWithoutPlacement =
    currentHotspotIndex + 1 < hotspotNodeIds.length &&
    hotspotNodeIds.every(
      (nodeId, index) => index <= currentHotspotIndex || !tree.nodes[nodeId].props.placementId,
    );

  const state = placement?.selector ? FormState.UPDATE : FormState.CREATE;
  const hasInitialValue = !!stepOneSelector;

  const [inEditMode, setInEditMode] = useState(state === 'create' || hasInitialValue);
  const [inputValue, setInputValue] = useState(stepOneSelector || placement?.selector);

  const { invalidSelector, querySelector } = checkInvalidSelector(inputValue);
  const notVisibleOnPage = !invalidSelector && !querySelector && !!inputValue;

  useEffect(() => {
    dispatch(setPreviewSelector(inputValue));
  }, [inputValue]);

  useEffect(() => {
    setInputValue(stepOneSelector || placement?.selector);
  }, [stepOneSelector]);

  useEffect(() => {
    if (inEditMode) {
      hotspotSelectorRef.current?.select();
      dispatch(setPreviewSelector(inputValue));
    } else {
      dispatch(setPreviewSelector(undefined));
    }

    // make sure that the editor is locked when editing a placement, but not when previewing it or if there is no snapshot
    if (!inEditMode && forcePlacementsTab) {
      dispatch(setForcePlacementsTab(false));
    } else if (inEditMode && !forcePlacementsTab) {
      dispatch(setForcePlacementsTab(true));
    }
  }, [inEditMode, forcePlacementsTab]);

  const onSetPlacement = (value: string) => {
    const selector: string = value;

    if (!selector) {
      setPlacement();
      return;
    }

    const newPlacement: TooltipPlacement = { selector };
    if (!placement) {
      addPlacement(newPlacement);
      return;
    }
    setPlacement(newPlacement);
  };

  const handleSave = () => {
    // HOTFIX: fall back to ID, ref seems to be broken here!
    const el = hotspotSelectorRef.current || document.getElementById('TOOLTIP_SELECTOR_INPUT');
    // @ts-ignore
    onSetPlacement(el?.value || '');
    setInEditMode(false);

    if (state === 'create' && !isLastHotspotsIdsWithoutPlacement) {
      // when first creating a placement, switch to Component tab on save
      dispatch(setToolboxTab(ToolboxTabs.Component));
    } else {
      if (isLastHotspotsIdsWithoutPlacement) {
        actions.setNodeEvent(EventTypes.selected, [hotspotNodeIds[currentHotspotIndex + 1]]);
      }

      onSave();
    }
  };

  const handleCancel = () => {
    if (state === 'update') {
      setInputValue(placement?.selector || '');
      setInEditMode(false);
      resetForm({ values: contentPlacement });
    } else if (state === 'create') {
      // delete newly created hotspot and go back to editing first hotspot
      deleteNode([hotspotId]);
      dispatch(setToolboxTab(ToolboxTabs.Component));
    }
  };

  if (inEditMode) {
    return (
      <>
        <SelectorInputWrapper>
          <ValueBox noBorder>
            <SelectorValueInput
              id="TOOLTIP_SELECTOR_INPUT"
              ref={hotspotSelectorRef}
              value={inputValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setInputValue(e.target.value || '');
              }}
              hasError={invalidSelector || !inputValue}
              hasWarning={notVisibleOnPage}
              onKeyDown={(e: KeyboardEvent) => {
                if (!inputValue) {
                  return;
                }

                if (
                  e.key === 'Enter' &&
                  !(state === 'update' && placement?.selector === inputValue)
                ) {
                  // if form submit button is not disabled, save change on Enter
                  handleSave();
                } else if (e.key === 'Escape') {
                  // cancel change on Escape
                  handleCancel();
                }
              }}
            />
            <RetargetButton
              appearance="outline"
              title="Retarget"
              onClick={() => {
                actions.setNodeEvent(EventTypes.selected, []);
                handleRetargetClick();
              }}
              variant="neutral"
              size="xsmall"
              data-cy="update-location"
            >
              <TargetIcon />
            </RetargetButton>
          </ValueBox>
          {(invalidSelector || !inputValue) && (
            <InlineMessage>
              <Feedback ml="xxs" color="danger" mode="alert" alertIcon={AlertDangerIcon}>
                {invalidSelector ? 'Selector is not valid' : 'Required'}
              </Feedback>
            </InlineMessage>
          )}
          {notVisibleOnPage && (
            <InlineMessage>
              <Feedback ml="xxs" color="warning" mode="alert" alertIcon={AlertWarningIcon}>
                Selector not visible on page
              </Feedback>
            </InlineMessage>
          )}
        </SelectorInputWrapper>
        <div>
          {isLastHotspotsIdsWithoutPlacement && (
            <Button
              variant="secondary"
              fullWidth
              disabled={!inputValue}
              onClick={handleSave}
              mt="xs"
            >
              Next
            </Button>
          )}
          {!isLastHotspotsIdsWithoutPlacement && (
            <Button
              variant="secondary"
              fullWidth
              disabled={!inputValue || (state === 'update' && placement?.selector === inputValue)}
              onClick={handleSave}
              mt="xs"
            >
              {state === 'create' ? 'Start Editing Content' : 'Update'}
            </Button>
          )}
          <Button variant="neutral" appearance="outline" fullWidth onClick={handleCancel} mt="xs">
            Cancel
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <CompletedStepSettings hasError={invalidSelector} hasWarning={notVisibleOnPage}>
        <EllipsisText content={inputValue}>
          <span>
            <strong>{inputValue}</strong>
          </span>
        </EllipsisText>
        <EditLinkButton
          appearance="text"
          inline
          size="small"
          variant="white"
          fontWeight="xbold"
          onClick={() => {
            setInEditMode(true);
            actions.setNodeEvent(EventTypes.selected, []);
          }}
        >
          edit
        </EditLinkButton>
      </CompletedStepSettings>
      {invalidSelector && (
        <InlineMessage>
          <Feedback ml="xxs" color="danger" mode="alert" alertIcon={AlertDangerIcon}>
            Selector is not valid
          </Feedback>
        </InlineMessage>
      )}
      {notVisibleOnPage && (
        <InlineMessage>
          <Feedback ml="xxs" color="warning" mode="alert" alertIcon={AlertWarningIcon}>
            Selector not visible on page
          </Feedback>
        </InlineMessage>
      )}
    </>
  );
};
